import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, isFavorite, toggleFavorite } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowUp, ArrowDown, Brain, Newspaper,
  Clock, CaretUp, CaretDown, Warning, ChartLine,
  Pulse, Target, Spinner, Equals, CurrencyBtc, TrendUp, Star, Copy, ArrowSquareOut, LinkSimple, CheckCircle, Bell
} from "@phosphor-icons/react";
import TradingViewChart from "@/components/TradingViewChart";


function SetAlertButton({ assetId, assetType, assetName, currentPrice }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState("above");
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    const target = parseFloat(price);
    if (!target || target <= 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/alerts`, {
        asset_id: assetId,
        asset_type: assetType,
        direction,
        price: target,
        label: `${assetName} ${direction} $${target.toLocaleString()}`,
      });
      toast.success("Alert created", {
        description: `We'll notify you when ${assetName} goes ${direction} $${target.toLocaleString()}`,
      });
      setOpen(false);
      setPrice("");
    } catch (err) {
      toast.error("Failed to create alert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen(o => !o);
          if (!price && currentPrice) setPrice(String(currentPrice.toFixed(currentPrice < 1 ? 6 : 2)));
        }}
        data-testid="set-alert-btn"
        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-xs font-medium rounded-sm border border-amber-500/40 transition-colors"
      >
        <Bell size={13} weight="bold" />
        Set Alert
      </button>
      {open && (
        <form
          onSubmit={submit}
          data-testid="alert-form"
          className="absolute right-0 mt-2 w-[260px] bg-zinc-950 border border-amber-500/30 rounded-sm shadow-xl z-50 p-3 space-y-2"
        >
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setDirection("above")}
              data-testid="alert-above"
              className={`flex-1 text-[10px] py-1.5 rounded-sm border transition-colors ${
                direction === "above"
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "bg-zinc-900 text-zinc-500 border-zinc-800"
              }`}
            >
              Above ↑
            </button>
            <button
              type="button"
              onClick={() => setDirection("below")}
              data-testid="alert-below"
              className={`flex-1 text-[10px] py-1.5 rounded-sm border transition-colors ${
                direction === "below"
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-zinc-900 text-zinc-500 border-zinc-800"
              }`}
            >
              Below ↓
            </button>
          </div>
          <div>
            <label className="text-[9px] uppercase text-zinc-500 block mb-1">Target Price</label>
            <input
              type="number"
              step="any"
              min="0"
              value={price}
              onChange={e => setPrice(e.target.value)}
              data-testid="alert-price-input"
              required
              autoFocus
              className="w-full text-xs font-data bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-zinc-100 outline-none focus:border-amber-500/50"
              placeholder="0.00"
            />
            {currentPrice > 0 && (
              <p className="text-[9px] text-zinc-600 mt-1">Current: ${currentPrice.toLocaleString()}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} className="flex-1 text-[10px] py-1.5 rounded-sm border border-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors">Cancel</button>
            <button type="submit" disabled={saving || !price} data-testid="alert-submit" className="flex-1 text-[10px] py-1.5 rounded-sm bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/50 disabled:opacity-40 transition-colors">
              {saving ? "Saving..." : "Create Alert"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function formatPrice(price) {
  if (!price && price !== 0) return "$0.00";
  if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.001) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(8)}`;
}

function IndicatorCard({ label, value, subtext, status }) {
  const statusColor = status === "bullish" ? "text-emerald-400" : status === "bearish" ? "text-red-400" : "text-zinc-400";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 p-3 rounded-sm" data-testid={`indicator-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-1">{label}</p>
      <p className={`text-sm font-data font-medium ${statusColor}`}>{value}</p>
      {subtext && <p className="text-[10px] text-zinc-600 mt-0.5">{subtext}</p>}
    </div>
  );
}

function PredictionRow({ timeframe, prediction }) {
  if (!prediction) return null;
  const dir = prediction.direction;
  const prob = prediction.probability;
  const isUp = dir === "up";
  const isDown = dir === "down";
  const bgColor = isUp ? "bg-emerald-500/8" : isDown ? "bg-red-500/8" : "bg-zinc-800/30";
  const borderColor = isUp ? "border-emerald-500/20" : isDown ? "border-red-500/20" : "border-zinc-700/40";
  const textColor = isUp ? "text-emerald-400" : isDown ? "text-red-400" : "text-zinc-400";

  // Radial gauge visual
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (prob / 100) * circumference;

  return (
    <div className={`${bgColor} border ${borderColor} rounded-sm p-3 flex items-center gap-3`} data-testid={`prediction-${timeframe}`}>
      {/* Mini radial gauge */}
      <div className="relative flex-shrink-0">
        <svg width="52" height="52" viewBox="0 0 52 52">
          <circle cx="26" cy="26" r={radius} fill="none" stroke="rgba(63,63,70,0.3)" strokeWidth="3" />
          <circle
            cx="26" cy="26" r={radius} fill="none"
            stroke={isUp ? "#10b981" : isDown ? "#ef4444" : "#71717a"}
            strokeWidth="3" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 26 26)"
            className="transition-all duration-500"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-data font-bold ${textColor}`}>
          {prob}%
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-data font-bold text-zinc-200">{timeframe}</span>
          <div className="flex items-center gap-1">
            {isUp && <CaretUp size={12} weight="fill" className="text-emerald-400" />}
            {isDown && <CaretDown size={12} weight="fill" className="text-red-400" />}
            {!isUp && !isDown && <Equals size={12} className="text-zinc-500" />}
            <span className={`text-[10px] font-data font-medium uppercase ${textColor}`}>
              {dir}
            </span>
          </div>
        </div>
        <p className="text-[9px] text-zinc-500 leading-relaxed line-clamp-2">{prediction.reasoning}</p>
      </div>
    </div>
  );
}

export default function AssetDetail() {
  const { type: assetType, id: rawId } = useParams();
  const assetId = decodeURIComponent(rawId);
  const navigate = useNavigate();
  const location = useLocation();

  // Read ?chain=... from the URL (set by SearchBar / favorites for DEX-only tokens).
  const chain = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get("chain") || "";
  }, [location.search]);
  const chainQs = chain ? `&chain=${encodeURIComponent(chain)}` : "";

  const [history, setHistory] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [news, setNews] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [chartDays, setChartDays] = useState("90");
  const [chartInterval, setChartInterval] = useState("1d");
  const [assetName, setAssetName] = useState(assetId);
  const [faved, setFaved] = useState(false);

  useEffect(() => {
    setFaved(isFavorite(assetId, assetType));
  }, [assetId, assetType]);

  // Resolve display name from recents/localStorage
  useEffect(() => {
    try {
      const recents = JSON.parse(localStorage.getItem("market_recents") || "[]");
      const found = recents.find(r => r.id === assetId && r.type === assetType);
      if (found) setAssetName(found.name || found.symbol || assetId);
    } catch {}
  }, [assetId, assetType]);

  const fetchData = useCallback(async () => {
    if (!assetId || !assetType) return;
    setLoading(true);
    const encodedId = encodeURIComponent(assetId);
    try {
      const [histResp, indResp, newsResp, priceResp] = await Promise.allSettled([
        axios.get(`${API}/asset/${assetType}/${encodedId}/history?days=${chartDays}&interval=${chartInterval}${chainQs}`),
        axios.get(`${API}/asset/${assetType}/${encodedId}/indicators?${chainQs.slice(1)}`),
        axios.get(`${API}/asset/${assetType}/${encodedId}/news?name=${encodeURIComponent(assetName)}&limit=10`),
        axios.get(`${API}/asset/${assetType}/${encodedId}/price?${chainQs.slice(1)}`),
      ]);

      if (histResp.status === "fulfilled") setHistory(histResp.value.data);
      if (indResp.status === "fulfilled") setIndicators(indResp.value.data?.indicators);
      if (newsResp.status === "fulfilled") setNews(newsResp.value.data);
      if (priceResp.status === "fulfilled") setPriceData(priceResp.value.data);

      try {
        const analysisResp = await axios.get(`${API}/asset/${assetType}/${encodedId}/analysis`);
        setAnalysis(analysisResp.data);
      } catch {}
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [assetId, assetType, chartDays, chartInterval, assetName, chainQs]);

  useEffect(() => {
    setHistory([]);
    setIndicators(null);
    setNews([]);
    setAnalysis(null);
    setPriceData(null);
    fetchData();
  }, [fetchData]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const resp = await axios.post(`${API}/asset/${assetType}/${encodeURIComponent(assetId)}/analysis?name=${encodeURIComponent(assetName)}`);
      setAnalysis(resp.data);
      toast.success("AI Analysis Complete");
    } catch (e) {
      toast.error("Analysis failed: " + (e.response?.data?.detail || "Unknown error"));
    } finally {
      setAnalyzing(false);
    }
  };

  const isPositive = priceData?.change_24h > 0;
  const currentPrice = priceData?.current_price;
  const ai = analysis?.analysis;
  const isCrypto = assetType === "crypto";

  const rsiStatus = indicators?.rsi > 70 ? "bearish" : indicators?.rsi < 30 ? "bullish" : "neutral";
  const macdStatus = indicators?.macd?.histogram > 0 ? "bullish" : "bearish";
  const stochStatus = indicators?.stochastic?.k > 80 ? "bearish" : indicators?.stochastic?.k < 20 ? "bullish" : "neutral";

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="asset-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {isCrypto ? <CurrencyBtc size={28} weight="bold" className="text-zinc-400" /> : <TrendUp size={28} weight="bold" className="text-zinc-400" />}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-bold text-zinc-50">{assetName}</h1>
              <button
                data-testid="fav-toggle-detail"
                onClick={() => {
                  const added = toggleFavorite({ id: assetId, symbol: assetId, name: assetName, type: assetType });
                  setFaved(added);
                }}
                className="p-1 rounded-sm hover:bg-zinc-800 transition-colors"
                title={faved ? "Remove from favorites" : "Add to favorites"}
              >
                <Star
                  size={18}
                  weight={faved ? "fill" : "regular"}
                  className={`transition-colors ${faved ? "text-amber-400" : "text-zinc-600 hover:text-zinc-400"}`}
                />
              </button>
              <Badge className="text-[10px] bg-zinc-800 text-zinc-400 border-0 rounded-sm">{assetId}</Badge>
              <Badge className={`text-[9px] px-1.5 py-0 border-0 rounded-sm ${isCrypto ? "bg-emerald-500/10 text-emerald-400" : "bg-blue-500/10 text-blue-400"}`}>
                {isCrypto ? "Crypto" : "Stock"}
              </Badge>
            </div>
            {/* Contract Address */}
            {isCrypto && priceData?.contract_address && (
              <div className="flex items-center gap-2 mt-1.5" data-testid="contract-address">
                <LinkSimple size={11} className="text-zinc-600 flex-shrink-0" />
                <span className="text-[10px] text-zinc-500 font-data">
                  {priceData.contract_address.slice(0, 6)}...{priceData.contract_address.slice(-4)}
                </span>
                <button
                  data-testid="copy-contract-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(priceData.contract_address);
                    toast.success("Contract address copied");
                  }}
                  className="p-0.5 rounded-sm hover:bg-zinc-800 transition-colors"
                  title="Copy full address"
                >
                  <Copy size={11} className="text-zinc-600 hover:text-zinc-300" />
                </button>
                {priceData.explorer_url && (
                  <a
                    href={priceData.explorer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="explorer-link"
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-zinc-800/60 hover:bg-zinc-700 text-[9px] text-zinc-400 hover:text-zinc-200 transition-colors"
                  >
                    <ArrowSquareOut size={10} />
                    {priceData.chain === "ethereum" ? "Etherscan" :
                     priceData.chain === "pulsechain" ? "PulseScan" :
                     priceData.chain === "solana" ? "Solscan" :
                     priceData.chain || "Explorer"}
                  </a>
                )}
                {priceData.chain && (
                  <Badge className="text-[8px] px-1 py-0 border-0 rounded-sm bg-zinc-800/60 text-zinc-500">
                    {priceData.chain}
                  </Badge>
                )}
              </div>
            )}
            {isCrypto && priceData?.explorer_url && !priceData?.contract_address && (
              <div className="flex items-center gap-2 mt-1.5" data-testid="explorer-only">
                <a
                  href={priceData.explorer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="explorer-link"
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-zinc-800/60 hover:bg-zinc-700 text-[9px] text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  <ArrowSquareOut size={10} />
                  View on {priceData.chain === "bitcoin" ? "Blockchair" : "Explorer"}
                </a>
              </div>
            )}
            {loading && !priceData ? (
              <Skeleton className="h-8 w-40 mt-1 bg-zinc-800" />
            ) : (
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-3xl font-data font-bold text-zinc-50" data-testid="current-price">
                  {formatPrice(currentPrice)}
                </span>
                {priceData?.change_24h !== undefined && (
                  <span className={`text-sm font-data font-medium ${isPositive ? "text-emerald-400" : "text-red-400"}`} data-testid="price-change">
                    {isPositive ? "+" : ""}{priceData.change_24h?.toFixed(2)}%
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <SetAlertButton assetId={assetId} assetType={assetType} assetName={assetName} currentPrice={currentPrice} />
          <button
            data-testid="run-analysis-btn"
            onClick={runAnalysis}
            disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:cursor-wait text-white text-xs font-medium rounded-sm transition-colors"
          >
            {analyzing ? <Spinner size={14} className="animate-spin" /> : <Brain size={14} weight="bold" />}
            {analyzing ? "Analyzing..." : "Run AI Analysis"}
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          {/* Chart */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 p-4 rounded-sm" data-testid="price-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-medium flex items-center gap-1.5">
                <ChartLine size={12} />Price Chart
              </h3>
              <div className="flex items-center gap-1 flex-wrap">
                {[
                  {v:"1",i:"5m",l:"5m"}, {v:"1",i:"30m",l:"30m"},
                  {v:"2",i:"1h",l:"1H"}, {v:"14",i:"4h",l:"4H"},
                  {v:"7",i:"1d",l:"1W"}, {v:"30",i:"1d",l:"1M"},
                  {v:"90",i:"1d",l:"3M"}, {v:"180",i:"1d",l:"6M"},
                  {v:"365",i:"1d",l:"1Y"},
                ].map(t => (
                  <button
                    key={t.l}
                    data-testid={`chart-tf-${t.l.toLowerCase()}`}
                    onClick={() => { setChartDays(t.v); setChartInterval(t.i); }}
                    className={`text-[10px] px-2 py-1 rounded-sm transition-colors ${
                      chartDays === t.v && chartInterval === t.i
                        ? "bg-zinc-700 text-zinc-200"
                        : "text-zinc-500 hover:text-zinc-300 bg-zinc-800/30"
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
            </div>
            {loading ? (
              <Skeleton className="h-[400px] bg-zinc-800/50" />
            ) : history.length > 0 ? (
              <TradingViewChart history={history} isPositive={isPositive} interval={chartInterval} />
            ) : (
              <div className="h-[400px] flex flex-col items-center justify-center gap-2" data-testid="no-chart-fallback">
                <ChartLine size={32} className="text-zinc-700" />
                <p className="text-xs text-zinc-500">No chart data available for this asset.</p>
                <p className="text-[10px] text-zinc-600">Try a longer timeframe or a different asset.</p>
              </div>
            )}
          </div>

          {/* Indicators */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 p-4 rounded-sm" data-testid="indicators-panel">
            <h3 className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-medium flex items-center gap-1.5 mb-3">
              <Pulse size={12} />Technical Indicators
            </h3>
            {loading || !indicators ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-16 bg-zinc-800/50" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <IndicatorCard label="RSI (14)" value={indicators.rsi} subtext={indicators.rsi > 70 ? "Overbought" : indicators.rsi < 30 ? "Oversold" : "Neutral"} status={rsiStatus} />
                <IndicatorCard label="MACD" value={indicators.macd?.line?.toFixed(4)} subtext={`Signal: ${indicators.macd?.signal?.toFixed(4)}`} status={macdStatus} />
                <IndicatorCard label="Stochastic %K" value={indicators.stochastic?.k?.toFixed(1)} subtext={`%D: ${indicators.stochastic?.d?.toFixed(1)}`} status={stochStatus} />
                <IndicatorCard label="ADX" value={indicators.adx?.toFixed(1)} subtext={indicators.adx > 25 ? "Strong Trend" : "Weak Trend"} status={indicators.adx > 25 ? "bullish" : "neutral"} />
                <IndicatorCard label="SMA 20" value={indicators.sma?.sma_20?.toLocaleString(undefined, {maximumFractionDigits: 4})} status="neutral" />
                <IndicatorCard label="SMA 50" value={indicators.sma?.sma_50?.toLocaleString(undefined, {maximumFractionDigits: 4}) || "N/A"} status="neutral" />
                <IndicatorCard label="EMA 12" value={indicators.ema?.ema_12?.toLocaleString(undefined, {maximumFractionDigits: 4})} status="neutral" />
                <IndicatorCard label="Bollinger Mid" value={indicators.bollinger_bands?.middle?.toLocaleString(undefined, {maximumFractionDigits: 4})} subtext={`U: ${indicators.bollinger_bands?.upper?.toLocaleString(undefined, {maximumFractionDigits: 2})} / L: ${indicators.bollinger_bands?.lower?.toLocaleString(undefined, {maximumFractionDigits: 2})}`} status="neutral" />
              </div>
            )}

            {indicators && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-zinc-950/50 border border-zinc-800/40 p-3 rounded-sm">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2 font-medium">Fibonacci Retracement</p>
                  <div className="space-y-1">
                    {indicators.fibonacci && Object.entries(indicators.fibonacci).map(([lvl, val]) => (
                      <div key={lvl} className="flex justify-between text-[10px]">
                        <span className="text-zinc-500">{lvl.replace("level_", "").replace("0", "0%").replace("236", "23.6%").replace("382", "38.2%").replace("500", "50%").replace("618", "61.8%").replace("786", "78.6%").replace("100", "100%")}</span>
                        <span className="text-zinc-300 font-data">{typeof val === "number" ? val.toLocaleString(undefined, {maximumFractionDigits: 6}) : val}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-zinc-950/50 border border-zinc-800/40 p-3 rounded-sm">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2 font-medium">Ichimoku Cloud</p>
                  <div className="space-y-1">
                    {indicators.ichimoku && Object.entries(indicators.ichimoku).map(([key, val]) => (
                      <div key={key} className="flex justify-between text-[10px]">
                        <span className="text-zinc-500">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span className="text-zinc-300 font-data">{typeof val === "number" ? val.toLocaleString(undefined, {maximumFractionDigits: 6}) : val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          {/* AI Analysis */}
          <div className={`border rounded-sm p-4 ${ai ? "bg-zinc-900/60 border-blue-500/20" : "bg-zinc-900/40 border-zinc-800/60"}`} data-testid="ai-analysis-panel">
            <h3 className="text-xs uppercase tracking-[0.1em] text-blue-400/80 font-medium flex items-center gap-1.5 mb-3">
              <Brain size={12} weight="bold" />AI Predictions
            </h3>

            {analyzing && (
              <div className="py-8 text-center ai-processing">
                <Spinner size={24} className="animate-spin text-blue-400 mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Grok is analyzing {assetName}...</p>
              </div>
            )}

            {!analyzing && !ai && (
              <div className="py-6 text-center">
                <Brain size={32} className="text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-500 mb-3">Click "Run AI Analysis" to generate predictions.</p>
              </div>
            )}

            {!analyzing && ai && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge className={`text-[10px] px-2 py-0.5 border-0 rounded-sm font-medium ${
                    ai.sentiment === "bullish" ? "bg-emerald-500/15 text-emerald-400"
                    : ai.sentiment === "bearish" ? "bg-red-500/15 text-red-400"
                    : "bg-zinc-700/50 text-zinc-400"
                  }`} data-testid="sentiment-badge">
                    {ai.sentiment?.toUpperCase()}
                  </Badge>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <svg width="40" height="40" viewBox="0 0 40 40">
                        <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(63,63,70,0.3)" strokeWidth="2.5" />
                        <circle
                          cx="20" cy="20" r="16" fill="none"
                          stroke={ai.sentiment === "bullish" ? "#10b981" : ai.sentiment === "bearish" ? "#ef4444" : "#71717a"}
                          strokeWidth="2.5" strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 16}
                          strokeDashoffset={2 * Math.PI * 16 - (ai.confidence / 100) * 2 * Math.PI * 16}
                          transform="rotate(-90 20 20)"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-data font-bold text-zinc-200">
                        {ai.confidence}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[9px] text-zinc-600 block">Confidence</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Warning size={12} className={ai.risk_level === "high" ? "text-red-400" : ai.risk_level === "medium" ? "text-amber-400" : "text-emerald-400"} />
                  <span className="text-[10px] text-zinc-500">Risk:</span>
                  <span className={`text-[10px] font-medium ${ai.risk_level === "high" ? "text-red-400" : ai.risk_level === "medium" ? "text-amber-400" : "text-emerald-400"}`}>
                    {ai.risk_level?.toUpperCase()}
                  </span>
                </div>

                <p className="text-xs text-zinc-300 leading-relaxed">{ai.analysis_summary}</p>

                <div className="bg-zinc-950/50 border border-zinc-800/40 p-2.5 rounded-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Newspaper size={10} className="text-zinc-500" />
                    <span className="text-[10px] text-zinc-500">News Impact:</span>
                    <Badge className={`text-[9px] px-1 py-0 border-0 rounded-sm ${
                      ai.news_impact === "high" ? "bg-red-500/15 text-red-400"
                      : ai.news_impact === "medium" ? "bg-amber-500/15 text-amber-400"
                      : "bg-zinc-700/50 text-zinc-500"
                    }`}>{ai.news_impact?.toUpperCase()}</Badge>
                  </div>
                  <p className="text-[10px] text-zinc-400 leading-relaxed">{ai.news_impact_detail}</p>
                </div>

                <div className="bg-zinc-950/50 border border-zinc-800/40 p-2.5 rounded-sm">
                  <p className="text-[10px] text-zinc-500 mb-1 flex items-center gap-1">
                    <Target size={10} />Trader Pattern
                  </p>
                  <p className="text-[10px] text-zinc-300 leading-relaxed">{ai.trader_pattern}</p>
                </div>

                {ai.key_levels && (
                  <div className="flex gap-2">
                    <div className="flex-1 bg-emerald-500/5 border border-emerald-500/10 p-2 rounded-sm text-center">
                      <p className="text-[9px] text-emerald-500/70 uppercase">Support</p>
                      <p className="text-xs font-data text-emerald-400">{formatPrice(ai.key_levels.support)}</p>
                    </div>
                    <div className="flex-1 bg-red-500/5 border border-red-500/10 p-2 rounded-sm text-center">
                      <p className="text-[9px] text-red-500/70 uppercase">Resistance</p>
                      <p className="text-xs font-data text-red-400">{formatPrice(ai.key_levels.resistance)}</p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2 font-medium">Forecast by Timeframe</p>
                  <div className="space-y-2">
                    {ai.predictions && ["1h", "4h", "1d", "1w", "1m"].map(tf => (
                      <PredictionRow key={tf} timeframe={tf.toUpperCase()} prediction={ai.predictions[tf]} />
                    ))}
                  </div>
                </div>

                {analysis?.created_at && (
                  <div className="flex items-center gap-1 text-[10px] text-zinc-600 pt-1">
                    <Clock size={10} />
                    <span>Generated: {new Date(analysis.created_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* News */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 p-4 rounded-sm" data-testid="news-panel">
            <h3 className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-medium flex items-center gap-1.5 mb-3">
              <Newspaper size={12} />Recent News
            </h3>
            {news.length > 0 ? (
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-2">
                  {news.map((article, i) => (
                    <a key={i} href={article.url} target="_blank" rel="noopener noreferrer"
                      data-testid={`news-article-${i}`}
                      className="block p-2.5 border border-zinc-800/40 hover:border-zinc-700 transition-colors rounded-sm group"
                    >
                      <p className="text-[11px] text-zinc-300 leading-relaxed line-clamp-2 group-hover:text-zinc-100">{article.title}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {article.source_type === "x_post" && article.x_handle ? (
                          <span className="text-[9px] text-zinc-300 flex items-center gap-0.5">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                            @{article.x_handle}
                            {article.verified && <CheckCircle size={8} weight="fill" className="text-blue-400" />}
                          </span>
                        ) : (
                          <span className="text-[9px] text-blue-400/70">{article.source}</span>
                        )}
                        {article.published_at && <span className="text-[9px] text-zinc-600">{new Date(article.published_at).toLocaleDateString()}</span>}
                      </div>
                    </a>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-xs text-zinc-600 py-4 text-center">No news available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
